import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import _ from 'lodash';
import Icon from 'components/Icon';
import CarouselV2 from 'componentsV2/Carousel';
import { additionalPosterEnabled, carouselV2Enabled } from 'helpers/ConfigHelper';
import {
  DESKTOP_MIN_BREAKPOINT,
  DESKTOP_BREAKPOINT,
  XL_BREAKPOINT,
} from 'helpers/BreakpointHelper';
import {
  Events,
  getSegmentModelMetadata,
  handleAnalytics,
  SegmentPageActionTypes,
  SegmentSources,
  SegmentUiElements,
} from '@foxcorp/delta-web-analytics';
import CarouselItem from './CarouselItem';
import CarouselPoster from './CarouselPoster';

import Style from './Carousel.module.scss';

const { MORE_EPISODES, SEARCH, WATCH } = SegmentPageActionTypes;
const { MORE_EPISODES: MORE_EPISODES_ELEMENT, PLAY, SERIES_TITLE } = SegmentUiElements;

class Carousel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      slideIndex: 0,
      slideCount: null,
    };
    this.nextSlide = this.nextSlide.bind(this);
    this.previousSlide = this.previousSlide.bind(this);
    this.shouldShowArrows = this.shouldShowArrows.bind(this);
    this.handleResize = this.handleResize.bind(this);

    window.addEventListener('resize', this.handleResize);
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { breakpoint, favoritesManager, headline, items } = this.props;
    const { slideIndex, slideCount } = this.state;
    const {
      breakpoint: newBreakpoint,
      favoritesManager: newFavoritesManager,
      headline: newHeadline,
      items: newItems,
    } = nextProps;

    const { slideIndex: newSlideIndex, slideCount: nextSlideCount } = nextState;

    if (!_.isEqual(favoritesManager, newFavoritesManager)) return true;
    if (!_.isEqual(items, newItems)) return true;
    if (breakpoint && newBreakpoint && breakpoint !== newBreakpoint) return true;
    if (headline && newHeadline && headline !== newHeadline) return true;
    if (slideIndex !== newSlideIndex) return true;
    if (slideCount !== nextSlideCount) return true;

    return false;
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize(event) {
    const windowWidth = event.target.innerWidth;
    const { slideCount } = this.state;
    const { noPreview } = this.props;

    if (!noPreview && windowWidth >= DESKTOP_MIN_BREAKPOINT && windowWidth < DESKTOP_BREAKPOINT) {
      if (slideCount !== 4) this.setState({ slideCount: 4 });
    }
    if (!noPreview && windowWidth >= DESKTOP_BREAKPOINT && windowWidth < XL_BREAKPOINT) {
      if (slideCount !== 5) this.setState({ slideCount: 5 });
    }
  }

  getPrevArrow() {
    const { slideIndex } = this.state;
    if (!this.shouldShowArrows() || slideIndex === 0) return null;
    const { roundArrows, carouselArrowOnClick } = this.props;
    const prevArrowIcon = roundArrows ? (
      <Icon color="white" viewBox="0 0 43 44" icon="carousel-arrow-left" ariaHidden />
    ) : (
      <Icon color="white" icon="arrow-left" ariaHidden />
    );
    return (
      <button
        className="slick-arrow slick-prev"
        type="button"
        data-role="none"
        aria-label="show previous content"
        onClick={() => {
          this.previousSlide();
          carouselArrowOnClick();
        }}
      >
        {prevArrowIcon}
      </button>
    );
  }

  getNextArrow(itemsLength) {
    const { slideIndex } = this.state;
    const { roundArrows, carouselArrowOnClick, slidesToShow } = this.props;
    const difference = itemsLength - slideIndex;
    if (!this.shouldShowArrows() || difference <= slidesToShow) return null;
    const nextArrowIcon = roundArrows ? (
      <Icon color="white" viewBox="0 0 43 44" icon="carousel-arrow-right" ariaHidden />
    ) : (
      <Icon color="white" icon="arrow-right" ariaHidden />
    );
    return (
      <button
        className="slick-arrow slick-next"
        type="button"
        aria-label="show next content"
        data-role="none"
        onClick={() => {
          this.nextSlide();
          carouselArrowOnClick();
        }}
      >
        {nextArrowIcon}
      </button>
    );
  }

  nextSlide() {
    this.slider.slickNext();
  }

  previousSlide() {
    this.slider.slickPrev();
  }

  shouldShowArrows() {
    const { items, showCarouselArrows, slidesToShow, useScrollableDiv, isPosters } = this.props;
    const itemsLength =
      isPosters && _.last(items).id === 'seeMore'
        ? _.get(items, 'length') - 1
        : _.get(items, 'length');
    const moreSlides = itemsLength > slidesToShow;
    const hideArrows = showCarouselArrows === false;
    const showArrows = useScrollableDiv || !items || hideArrows ? false : moreSlides;
    return showArrows;
  }

  seoHeaderWrapper = (seoElement, isFirst) => {
    if (isFirst) {
      return <h1>{seoElement}</h1>;
    }
    return seoElement;
  };

  renderItems() {
    const {
      addCarouselPlaceholders,
      applyInlineBackground,
      breakpoint,
      collectionCount,
      collectionName,
      playlistLength,
      collectionType,
      collectionVerticalPosition,
      descriptionLines,
      disableHoverStateActionButtons,
      favoritesManager,
      headline,
      isHoverStateV2,
      isPersonality,
      isPosters,
      isSeriesDetailAnonymous,
      isSeriesDetailPage,
      isSeasons,
      items,
      noPreview,
      onTileClick,
      overrideAdditionalPosterStyle,
      overrideTheme,
      panel,
      panelCount,
      processor,
      renderImage,
      searchCompleted,
      setSearchCompleted,
      searchTerm,
      shouldDisplayNetworkLogo,
      shouldInvertTitleOrder,
      showUpgradeCTA,
      slidesToShow,
      subtitleLines,
      truncateSubtitle,
      theme,
      tileTheme,
      upcomingOverlay,
      verticalPosition,
      isLiveTileV2,
    } = this.props;
    const { slideIndex } = this.state;
    const itemsLength = items && items.length;
    if (!items || itemsLength === 0) return null;
    const { global_moreInfoButton: moreInfoText } = window.Language;
    const remainder = slideIndex % itemsLength;
    const areBadgesDisabled = panel?.areBadgesDisabled;
    let itemToScaleFromLeft;
    let itemToScaleFromRight;
    if (remainder === 0) {
      itemToScaleFromRight = slidesToShow - 1;
    } else if (remainder >= slidesToShow) {
      const remainingItems = itemsLength - slideIndex;
      if (remainingItems > slidesToShow) {
        itemToScaleFromLeft = slideIndex;
        itemToScaleFromRight = slidesToShow + slideIndex - 1;
      } else {
        itemToScaleFromLeft = itemsLength - slidesToShow;
      }
    }
    const carouselItems = items.map((item, idx) => {
      const customizedItem = {
        ...item,
        itemIndex: idx,
        slideIndex,
      };
      const processed = processor(customizedItem);
      if (processed.link === 'undefined') return null;
      const { description, link, isRedirect, overlayProps = {}, subtitle } = processed;

      const segmentPlaylistButtonAction = (props = {}) => {
        const { currentSeason, name, seriesName, showCode, uID } = item;
        const seasonNumber = currentSeason;
        const modelMetaData = item?.trackingData?.properties?.modelMetaData;
        let collectionTitle = null;
        if (headline) {
          collectionTitle =
            typeof headline === 'string'
              ? headline
              : _.get(headline, 'props.clipsSections[0].name');
        } else if (isSeriesDetailPage && isSeasons) {
          collectionTitle = `Season ${seasonNumber}`;
        }
        const itemSubtitle = _.isString(subtitle) ? subtitle : description;
        const { actionType = '', uiElement = '' } = props;
        const actionLink =
          uiElement &&
          (uiElement === SERIES_TITLE || uiElement === MORE_EPISODES_ELEMENT) &&
          MORE_EPISODES;
        const actionSearch = !itemSubtitle && SEARCH;
        const pageActionType = actionSearch || actionLink || actionType || WATCH;
        const actionLocation = collectionName || SegmentSources.HOME;
        handleAnalytics({
          event: Events.PLAYLIST_BUTTON_ACTION,
          payload: {
            page_action_type: pageActionType,
            page_action_location: isSeriesDetailPage ? SegmentSources.DETAIL : actionLocation,
            page_collection_count: collectionCount,
            page_collection_id: panel?.id,
            page_collection_title: collectionTitle,
            page_item_horizontal_position: idx + 1,
            page_item_vertical_position: isSeriesDetailPage
              ? verticalPosition - 1
              : verticalPosition || collectionVerticalPosition,
            page_item_title: seriesName || name,
            page_item_subtitle: itemSubtitle,
            page_playlist_length: playlistLength || _.get(this.props, 'items.length', 0),
            page_recs_collection: panel?.recommended,
            page_recs_collection_model_id: modelMetaData?.modelId ?? ' ',
            page_recs_collection_request_id: modelMetaData?.requestId ?? ' ',
            page_recs_collection_result_set_id: modelMetaData?.resultSetId ?? ' ',
            page_recs_collection_source_id: modelMetaData?.sourceId ?? ' ',
            page_ui_element: uiElement || PLAY,
            page_video_uid: uID || showCode,
            ...getSegmentModelMetadata(),
          },
          callback: () => {
            window.DataLayer.referringCollection = collectionTitle;
          },
        });
      };

      const handleClick = (opts = {}) => {
        const { uiElement } = opts;
        const {
          title: titleText,
          description: fallbackSubtitle,
          subtitle: subtitleText,
          playButtonText,
          titleLink,
        } = overlayProps;
        const eventAction = isRedirect
          ? 'Exit Link'
          : playButtonText || (titleLink ? moreInfoText : undefined);
        onTileClick({
          item,
          idx,
          isRedirect,
          titleText,
          subtitleText: subtitleText || fallbackSubtitle,
          eventAction,
          link,
          uiElement,
        });

        segmentPlaylistButtonAction({ uiElement });
      };
      const key = `${item.id}-${idx}`;
      const scaleFromLeft = idx === itemToScaleFromLeft;
      const scaleFromRight = idx === itemToScaleFromRight;
      const itemProps = {
        applyInlineBackground,
        areBadgesDisabled,
        breakpoint,
        collectionType,
        favoritesManager,
        item,
        itemIndex: idx,
        onTileClick: handleClick,
        processed,
        renderImage,
        headline,
        isHoverStateV2,
        isPersonality,
        isSeriesDetailPage,
        isSeasons,
        disableHoverStateActionButtons,
        panelCount,
        scaleFromLeft,
        scaleFromRight,
        searchTerm,
        searchCompleted,
        setSearchCompleted,
        segmentPlaylistButtonAction,
        shouldDisplayNetworkLogo,
        shouldInvertTitleOrder,
        showUpgradeCTA,
        subtitleLines,
        truncateSubtitle,
        tileTheme,
        upcomingOverlay,
        isLiveTileV2,
      };
      const posterProps = {
        breakpoint,
        collectionCount,
        descriptionLines,
        favoritesManager,
        guid: item.guid,
        id: item.id,
        isHoverStateV2,
        isSeriesDetailAnonymous,
        scaleFromLeft,
        scaleFromRight,
        item,
        itemIndex: idx,
        maxItems: items.length,
        noPreview,
        overrideAdditionalPosterStyle,
        onPosterClick: handleClick,
        onTileClick: handleClick,
        overrideTheme,
        processed,
        renderImage,
        segmentPlaylistButtonAction,
        theme,
      };
      if (isPosters) {
        const poster = (
          <div
            className={classNames(theme.carouselPosterContainer, Style.carouselPosterContainer)}
            key={key}
          >
            <CarouselPoster {...posterProps} />
          </div>
        );
        return item.id === 'seeMore' ? null : poster;
      }
      return (
        <div
          className={classNames(theme.carouselItemContainer, Style.carouselItemContainer)}
          key={key}
        >
          <CarouselItem {...itemProps} />
        </div>
      );
    });
    if (addCarouselPlaceholders && itemsLength < slidesToShow) {
      const placeholderCount = slidesToShow - itemsLength;
      for (let i = 0; i < placeholderCount; i += 1) {
        carouselItems.push(<div key={`placeholder-${i}`} className="carouselPlaceholder" />);
      }
    }
    return carouselItems;
  }

  renderHeader() {
    const {
      featuredTitleImage,
      headline,
      seeAll,
      isFirst,
      isGenreCollection,
      isSeasons,
      theme: defaultTheme,
      overrideTheme,
    } = this.props;
    const theme = overrideTheme || defaultTheme;
    let header = null;
    if (featuredTitleImage) {
      header = this.seoHeaderWrapper(
        <div className={theme.imageOnly}>
          <img className={theme.headlineImg} src={featuredTitleImage} alt={headline} />
        </div>,
        isFirst,
      );
    } else if (headline) {
      header = (
        <h2
          aria-label={`${headline} heading`}
          className={classNames([
            theme.headline,
            isSeasons && theme.seasonHeadline,
            isGenreCollection && theme.genreHeadline,
          ])}
        >
          {headline}
        </h2>
      );
    } else {
      header = <div className={classNames(theme.emptyHeader, Style.emptyHeader)} />;
    }

    if (seeAll) {
      header = (
        <div className={theme.headlineWithSeeAll}>
          {header}
          {seeAll}
        </div>
      );
    }

    return header;
  }

  renderSliderDesktop() {
    const {
      allowScrollLock,
      autoplaySpeed,
      disableScroll,
      isInfinite,
      isPosters,
      isSeriesDetailAnonymous,
      isSeriesDetailPage,
      items,
      panel,
      slidesToScroll,
      slidesToShow: originalSlidesToShow,
      updateVisibleSlides,
    } = this.props;
    const { slideCount } = this.state;

    const slidesToShow = isPosters && slideCount ? slideCount : originalSlidesToShow;
    const { responsive } = this.props;
    const { slideIndex } = this.state;
    let autoplaySettings = {};
    if (autoplaySpeed) {
      autoplaySettings = {
        autoplay: true,
        autoplaySpeed,
      };
    }
    const useTransform = true;
    const useCSS = true;
    const itemsLength = _.get(items, 'length');
    const isHalfWay = Math.floor(itemsLength / 2) <= slideIndex;
    if (carouselV2Enabled()) {
      let carouselType = isPosters ? 'poster' : 'tile';
      if (isSeriesDetailPage) carouselType = isPosters ? 'poster-series' : 'tile-series';
      if (isSeriesDetailAnonymous) carouselType = isPosters ? 'poster-series' : 'tile-series';
      return (
        <>
          {this.renderHeader()}
          <CarouselV2 type={carouselType}>{this.renderItems()}</CarouselV2>
        </>
      );
    }
    const settings = {
      afterChange: () => {
        if (allowScrollLock) {
          disableScroll({ disableScroll: false, bodyOnly: true });
        }
      },
      arrows: false,
      draggable: this.shouldShowArrows(),
      dots: false,
      beforeChange: (prevSlide, nextSlide) => {
        this.setState({ slideIndex: nextSlide });
        updateVisibleSlides(prevSlide, nextSlide, panel, isHalfWay);
      },
      infinite: isInfinite,
      responsive,
      slidesToShow,
      speed: 500,
      test: 'carousel-wrapper',
      slidesToScroll,
      ...autoplaySettings,
      useTransform,
      useCSS,
      lazyLoad: true,
    };
    return (
      <>
        {this.renderHeader()}
        <Slider
          ref={s => {
            this.slider = s;
          }}
          {...settings}
          trayProps={{ role: 'listbox' }}
          data-test="shared-carousel"
        >
          {this.renderItems()}
        </Slider>
      </>
    );
  }

  renderSliderMobile() {
    const { theme: defaultTheme, overrideTheme } = this.props;
    const theme = overrideTheme || defaultTheme;
    return (
      <div>
        {this.renderHeader()}
        <div className={theme.scrollableDiv}>{this.renderItems()}</div>
      </div>
    );
  }

  render() {
    const {
      isPosters,
      isGenreCollection,
      items,
      roundArrows,
      slidesToShow,
      theme,
      useScrollableDiv,
      overrideAdditionalPosterStyle,
    } = this.props;
    const itemsLength = _.get(items, 'length');
    const isCarouselV2 = carouselV2Enabled();
    const sliderContainer = [
      theme.container,
      Style.container,
      theme.redesignCollections,
      Style.redesignCollections,
      isCarouselV2 && Style.carouselV2,
    ];

    if (slidesToShow > itemsLength) {
      sliderContainer.push(theme.justifyLeft, Style.justifyLeft);
    }
    if (roundArrows) sliderContainer.push(theme.roundArrows, Style.roundArrows);
    const slider = useScrollableDiv ? this.renderSliderMobile() : this.renderSliderDesktop();
    return (
      <div
        className={classNames([
          theme.carouselContainer,
          Style.carouselContainer,
          isPosters && [theme.carouselPosterWrapper, Style.carouselPosterWrapper],
          additionalPosterEnabled() && !overrideAdditionalPosterStyle && theme.additionalPoster,
          isGenreCollection && Style.genreCollectionContainer,
        ])}
      >
        {!isCarouselV2 && this.getPrevArrow()}
        <div className={classNames(sliderContainer)}>{slider}</div>
        {!isCarouselV2 && this.getNextArrow(itemsLength)}
      </div>
    );
  }
}
Carousel.defaultProps = {
  addCarouselPlaceholders: false,
  allowScrollLock: false,
  applyInlineBackground: false,
  autoplaySpeed: null,
  breakpoint: null,
  carouselArrowOnClick: () => {},
  collectionCount: null,
  playlistLength: null,
  collectionName: null,
  collectionType: null,
  collectionVerticalPosition: null,
  descriptionLines: 5,
  disableHoverStateActionButtons: false,
  disableScroll: () => {},
  favoritesManager: null,
  featuredTitleImage: null,
  headline: null,
  isFirst: false,
  isGenreCollection: false,
  isHoverStateV2: false,
  isInfinite: true,
  isPosters: false,
  isSeriesDetailAnonymous: false,
  isSeriesDetailPage: false,
  items: [],
  noPreview: false,
  onPosterClick: () => {},
  onTileClick: () => {},
  overrideAdditionalPosterStyle: false,
  overrideTheme: null,
  panel: {},
  panelCount: 0,
  processor: item => item,
  renderImage: () => {},
  roundArrows: false,
  searchCompleted: false,
  setSearchCompleted: () => {},
  searchTerm: '',
  segmentPlaylistButtonAction: _.noop,
  shouldDisplayNetworkLogo: false,
  shouldInvertTitleOrder: () => {},
  showCarouselArrows: true,
  slidesToScroll: 1,
  slidesToShow: 3,
  theme: {},
  tileTheme: {},
  upcomingOverlay: () => {},
  updateVisibleSlides: () => {},
  useScrollableDiv: false,
  isLiveTileV2: false,
  seeAll: null,
};
Carousel.propTypes = {
  addCarouselPlaceholders: PropTypes.bool,
  allowScrollLock: PropTypes.bool,
  applyInlineBackground: PropTypes.bool,
  autoplaySpeed: PropTypes.number,
  breakpoint: PropTypes.string,
  carouselArrowOnClick: PropTypes.func,
  collectionCount: PropTypes.number,
  playlistLength: PropTypes.number,
  collectionName: PropTypes.string,
  collectionType: PropTypes.string,
  collectionVerticalPosition: PropTypes.number,
  descriptionLines: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disableHoverStateActionButtons: PropTypes.bool,
  disableScroll: PropTypes.func,
  favoritesManager: PropTypes.shape({}),
  featuredTitleImage: PropTypes.string,
  headline: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  isFirst: PropTypes.bool,
  isGenreCollection: PropTypes.bool,
  isInfinite: PropTypes.bool,
  isHoverStateV2: PropTypes.bool,
  isPersonality: PropTypes.bool,
  isPosters: PropTypes.bool,
  isSeriesDetailAnonymous: PropTypes.bool,
  isSeriesDetailPage: PropTypes.bool,
  isSeasons: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  noPreview: PropTypes.bool,
  onPosterClick: PropTypes.func,
  onTileClick: PropTypes.func,
  overrideAdditionalPosterStyle: PropTypes.bool,
  overrideTheme: PropTypes.shape({}),
  panel: PropTypes.shape({}),
  panelCount: PropTypes.number,
  processor: PropTypes.func,
  renderImage: PropTypes.func,
  responsive: PropTypes.arrayOf(PropTypes.shape({})),
  roundArrows: PropTypes.bool,
  segmentPlaylistButtonAction: PropTypes.func,
  searchCompleted: PropTypes.bool,
  setSearchCompleted: PropTypes.func,
  searchTerm: PropTypes.string,
  shouldDisplayNetworkLogo: PropTypes.bool,
  shouldInvertTitleOrder: PropTypes.func,
  showCarouselArrows: PropTypes.bool,
  showUpgradeCTA: PropTypes.bool,
  slidesToScroll: PropTypes.number,
  slidesToShow: PropTypes.number,
  subtitleLines: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  truncateSubtitle: PropTypes.bool,
  theme: PropTypes.shape({}),
  tileTheme: PropTypes.shape(),
  upcomingOverlay: PropTypes.func,
  updateVisibleSlides: PropTypes.func,
  useScrollableDiv: PropTypes.bool,
  verticalPosition: PropTypes.number,
  isLiveTileV2: PropTypes.bool,
  seeAll: PropTypes.node,
};

export default Carousel;
